import React, { useContext, useState, useEffect, useMemo } from "react";
import Equipments from "./Equipments";
import DefaultFee from "./DefaultFee";
import { Stack } from "@mui/material";
import PercentageInput from "./input-elements/PercentageInput";
import CurrencyInput from "./input-elements/CurrencyInput";
import ToggleSwitch from "./input-elements/ToggleSwitch";
import { EquipmentContext } from "../useContext/context";
import * as Yup from "yup";
import { Form } from "react-bootstrap";
import pricingValidation from "../pricing-validation";
import ToggleSwitchContainer from "./input-elements/ToggleSwitchContainer";
import { EMS_OTHER_DEFAULT_FEE } from "../../../../constants";
import { FaCaretDown } from "react-icons/fa";
import { IoMdCheckmark } from "react-icons/io";
import ACHPricing from "./ACHPricing";

const surcharge_validation = pricingValidation.pricingValidation.surcharge;

const Surcharge = ({
    selectedEquipments,
    emsEquipments,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError,
}) => {
    const [showDebit, setShowDebit] = useState(false);
    const { setErrors, pricingDefaults } = useContext(EquipmentContext);
    const [surchargeDefaults, setSurchargeDefaults] = useState(surcharge_validation);

    // Update surcharge defaults when pricing defaults load
    useEffect(() => {
        if (pricingDefaults && pricingDefaults.surcharge) {
            setSurchargeDefaults(pricingDefaults.surcharge);
        }
    }, [pricingDefaults]);

    // Create dynamic validation schema based on current defaults
    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            surcharge_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Surcharge Rate is required")
                .min(
                    surchargeDefaults.surcharge_rate?.minimum || surcharge_validation.surcharge_rate.minimum,
                    `Please enter a rate of ${(surchargeDefaults.surcharge_rate?.minimum || surcharge_validation.surcharge_rate.minimum).toFixed(2)}% or higher.`,
                ),

            surcharge_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Per Transaction fee is required")
                .min(
                    surchargeDefaults.surcharge_per_transaction?.minimum || surcharge_validation.surcharge_per_transaction.minimum,
                    `Please enter a fee of $${(surchargeDefaults.surcharge_per_transaction?.minimum || surcharge_validation.surcharge_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            surcharge_monthly_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Monthly Fee is required")
                .min(
                    surchargeDefaults.surcharge_monthly_fee?.minimum || surcharge_validation.surcharge_monthly_fee.minimum,
                    `Please enter a fee of $${(surchargeDefaults.surcharge_monthly_fee?.minimum || surcharge_validation.surcharge_monthly_fee.minimum).toFixed(2)} or higher.`,
                ),

            surcharge_signature_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Signature Debit Rate is required")
                .when("pricing_model", {
                    is: "ic_plus",
                    then: Yup.number()
                        .required("Signature Debit Rate is required")
                        .min(
                            surchargeDefaults.surcharge_signature_debit_rate?.minimum || surcharge_validation.surcharge_signature_debit_rate.minimum,
                            `Please enter a rate of ${(surchargeDefaults.surcharge_signature_debit_rate?.minimum || surcharge_validation.surcharge_signature_debit_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),
            surcharge_signature_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("pricing_model", {
                    is: "ic_plus",
                    then: Yup.number()
                        .required("Signature Debit Per Transaction fee is required")
                        .min(
                            surchargeDefaults.surcharge_signature_debit_per_transaction?.minimum || surcharge_validation.surcharge_signature_debit_per_transaction.minimum,
                            `Please enter a fee of $${(surchargeDefaults.surcharge_signature_debit_per_transaction?.minimum || surcharge_validation.surcharge_signature_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            surcharge_qual_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("pricing_model", {
                    is: "tiered",
                    then: Yup.number()
                        .required("Qualified Debit Rate is required")
                        .min(
                            surchargeDefaults.surcharge_qual_debit_rate?.minimum || surcharge_validation.surcharge_qual_debit_rate.minimum,
                            `Please enter a rate of ${(surchargeDefaults.surcharge_qual_debit_rate?.minimum || surcharge_validation.surcharge_qual_debit_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            surcharge_qual_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("pricing_model", {
                    is: "tiered",
                    then: Yup.number()
                        .required("Qualified Debit Per Transaction fee is required")
                        .min(
                            surchargeDefaults.surcharge_qual_debit_per_transaction?.minimum || surcharge_validation.surcharge_qual_debit_per_transaction.minimum,
                            `Please enter a fee of $${(surchargeDefaults.surcharge_qual_debit_per_transaction?.minimum || surcharge_validation.surcharge_qual_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            surcharge_mid_qual_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("pricing_model", {
                    is: "tiered",
                    then: Yup.number()
                        .required("Mid-Qualified Debit Rate is required")
                        .min(
                            surchargeDefaults.surcharge_mid_qual_debit_rate?.minimum || surcharge_validation.surcharge_mid_qual_debit_rate.minimum,
                            `Please enter a rate of ${(surchargeDefaults.surcharge_mid_qual_debit_rate?.minimum || surcharge_validation.surcharge_mid_qual_debit_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            surcharge_mid_qual_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("pricing_model", {
                    is: "tiered",
                    then: Yup.number()
                        .required("Mid-Qualified Debit Per Transaction fee is required")
                        .min(
                            surchargeDefaults.surcharge_mid_qual_debit_per_transaction?.minimum || surcharge_validation.surcharge_mid_qual_debit_per_transaction.minimum,
                            `Please enter a fee of $${(surchargeDefaults.surcharge_mid_qual_debit_per_transaction?.minimum || surcharge_validation.surcharge_mid_qual_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            surcharge_non_qual_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("pricing_model", {
                    is: "tiered",
                    then: Yup.number()
                        .required("Non-Qualified Debit Rate is required")
                        .min(
                            surchargeDefaults.surcharge_non_qual_debit_rate?.minimum || surcharge_validation.surcharge_non_qual_debit_rate.minimum,
                            `Please enter a rate of ${(surchargeDefaults.surcharge_non_qual_debit_rate?.minimum || surcharge_validation.surcharge_non_qual_debit_rate.minimum).toFixed(2)}% or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            surcharge_non_qual_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("pricing_model", {
                    is: "tiered",
                    then: Yup.number()
                        .required("Non-Qualified Debit Per Transaction fee is required")
                        .min(
                            surchargeDefaults.surcharge_non_qual_debit_per_transaction?.minimum || surcharge_validation.surcharge_non_qual_debit_per_transaction.minimum,
                            `Please enter a fee of $${(surchargeDefaults.surcharge_non_qual_debit_per_transaction?.minimum || surcharge_validation.surcharge_non_qual_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                        ),
                    otherwise: Yup.number().notRequired(),
                }),

            surcharge_pin_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Rate is required")
                .min(
                    surchargeDefaults.surcharge_pin_debit_rate?.minimum || surcharge_validation.surcharge_pin_debit_rate.minimum,
                    `Please enter a rate of ${(surchargeDefaults.surcharge_pin_debit_rate?.minimum || surcharge_validation.surcharge_pin_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),

            surcharge_pin_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Per Transaction fee is required")
                .min(
                    surchargeDefaults.surcharge_pin_debit_per_transaction?.minimum || surcharge_validation.surcharge_pin_debit_per_transaction.minimum,
                    `Please enter a fee of $${(surchargeDefaults.surcharge_pin_debit_per_transaction?.minimum || surcharge_validation.surcharge_pin_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            fnf_number: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("ebt", {
                    is: "yes",
                    then: Yup.number().required("FNF Number is Required"),
                    otherwise: Yup.number(),
                }),
        });
    }, [surchargeDefaults]);

    const handleOnChange = (e) => {
        try {
            e?.persist();
        } catch (error) {}
        const updatedFormData = {
            ...form_data,
            [e.target.name]: e.target.value,
        };

        setFormDataByKeyValue(e.target.name, e.target.value);
        validationSchema
            .validateAt(e.target.name, updatedFormData)
            .then((e) => {
                // nothing here
                setErrors({});
            })
            .catch((err) => {
                let validation_errors = errors;
                validation_errors = {
                    ...validation_errors,
                    [e.target.name]: err.errors,
                };
                setErrors(validation_errors);
            });
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setShowDebit(!showDebit);
        }
    };

    return (
        <div className="dual-pricing-section">
            <p className="sub-heading-lead">
                Set Your preferred Surcharge rate
            </p>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                className="container-col-2 mt-2r mb-2r"
            >
                <div className="w-100">
                    <PercentageInput
                        label="Surcharge Rate"
                        type="number"
                        placeholder={surchargeDefaults.surcharge_rate?.default}
                        name={"surcharge_rate"}
                        onChange={handleOnChange}
                        value={form_data.surcharge_rate}
                        errors={errors}
                        softLimit={surchargeDefaults?.surcharge_rate?.soft_limit}
                    />
                    {errors?.surcharge_rate &&
                        Array.isArray(errors.surcharge_rate) &&
                        errors.surcharge_rate.map((error) => (
                            <span className="error-message">{error}</span>
                        ))}
                </div>
                <div className="w-100">
                    <CurrencyInput
                        label="Per Txn"
                        type="number"
                        placeholder={surchargeDefaults.surcharge_per_transaction?.default}
                        name={"surcharge_per_transaction"}
                        onChange={handleOnChange}
                        value={form_data.surcharge_per_transaction}
                        errors={errors}
                        softLimit={surchargeDefaults?.surcharge_per_transaction?.soft_limit}
                    />
                    {errors?.surcharge_per_transaction &&
                        Array.isArray(errors.surcharge_per_transaction) &&
                        errors.surcharge_per_transaction.map((error) => (
                            <span className="error-message">{error}</span>
                        ))}
                </div>
                <div className="w-100">
                    <CurrencyInput
                        label="Monthly Fee"
                        type="number"
                        placeholder={surchargeDefaults.surcharge_monthly_fee?.default}
                        name={"surcharge_monthly_fee"}
                        onChange={handleOnChange}
                        value={form_data.surcharge_monthly_fee}
                        errors={errors}
                        softLimit={surchargeDefaults?.surcharge_monthly_fee?.soft_limit}
                    />
                    {errors?.surcharge_monthly_fee &&
                        Array.isArray(errors.surcharge_monthly_fee) &&
                        errors.surcharge_monthly_fee.map((error) => (
                            <span className="error-message">{error}</span>
                        ))}
                </div>
            </Stack>
            {/*
      <div className="equipment-box mt-2r" >
        <p className="box-label" >Signature Debit</p>
        <div className="container-col-2 mb-1r mt-2r" >
          <div className="w-100">
            <PercentageInput label="Rate" type="number" placeholder="3.00" name={"surcharge_signature_debit_rate"} onChange={handleOnChange} value={form_data.surcharge_signature_debit_rate} errors={errors}  />
            {errors?.surcharge_signature_debit_rate && Array.isArray(errors.surcharge_signature_debit_rate) && errors.surcharge_signature_debit_rate.map(error => (
              <span className="error-message" >{error}</span>
            ))}
          </div>
          <div className="w-100">
            <CurrencyInput label="Per Txn" type="number" placeholder="0.15" name={"surcharge_signature_debit_per_transaction"} onChange={handleOnChange} value={form_data.surcharge_signature_debit_per_transaction} errors={errors}  />
            {errors?.surcharge_signature_debit_per_transaction && Array.isArray(errors.surcharge_signature_debit_per_transaction) && errors.surcharge_signature_debit_per_transaction.map(error => (
              <span className="error-message" >{error}</span>
            ))}
          </div>
        </div>
      </div> */}

            <div className="w-fit-content mt-2r default-fee">
                <h5
                    tabIndex="0"
                    role="button"
                    onKeyDown={handleKeyDown}
                    onClick={(_e) => setShowDebit(!showDebit)}
                >
                    {showDebit ? "Hide Debit Fees" : "View Debit Fees"}
                    <FaCaretDown
                        style={showDebit ? { transform: "rotate(180deg)" } : {}}
                    />
                </h5>
            </div>
            {showDebit && (
                <>
                    <p className="sub-heading-lead mt-4r">
                        Select your preferred Debit structure
                    </p>
                    <div className="pricing-model-buttons ">
                        <button
                            type="button"
                            className={
                                form_data.pricing_model == "tiered"
                                    ? "active"
                                    : ""
                            }
                            onClick={() => {
                                setFormDataByKeyValue(
                                    "pricing_model",
                                    "tiered",
                                );
                                setErrors({});
                            }}
                        >
                            {form_data.pricing_model == "tiered" ? (
                                <IoMdCheckmark />
                            ) : (
                                ""
                            )}{" "}
                            Tiered Rate
                        </button>
                        <button
                            type="button"
                            className={
                                form_data.pricing_model == "ic_plus"
                                    ? "active"
                                    : ""
                            }
                            onClick={() => {
                                setFormDataByKeyValue(
                                    "pricing_model",
                                    "ic_plus",
                                );
                                setErrors({});
                            }}
                        >
                            {form_data.pricing_model == "ic_plus" ? (
                                <IoMdCheckmark />
                            ) : (
                                ""
                            )}{" "}
                            IC+
                        </button>
                    </div>

                    {form_data.pricing_model == "ic_plus" && (
                        <div className="equipment-box mt-2r">
                            <p className="box-label">Signature Debit</p>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                className="container-col-2 mb-1r mt-2r"
                            >
                                <div className="w-100">
                                    <PercentageInput
                                        label="IC+ Rate"
                                        type="number"
                                        placeholder={surchargeDefaults.surcharge_signature_debit_rate?.default}
                                        name={"surcharge_signature_debit_rate"}
                                        onChange={handleOnChange}
                                        value={
                                            form_data.surcharge_signature_debit_rate
                                        }
                                        errors={errors}
                                        softLimit={surchargeDefaults?.surcharge_signature_debit_rate?.soft_limit}
                                    />
                                    {errors?.surcharge_signature_debit_rate &&
                                        Array.isArray(
                                            errors.surcharge_signature_debit_rate,
                                        ) &&
                                        errors.surcharge_signature_debit_rate.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                                <div className="w-100">
                                    <CurrencyInput
                                        label="IC+ Txn"
                                        type="number"
                                        placeholder={surchargeDefaults.surcharge_signature_debit_per_transaction?.default}
                                        name={
                                            "surcharge_signature_debit_per_transaction"
                                        }
                                        onChange={handleOnChange}
                                        value={
                                            form_data.surcharge_signature_debit_per_transaction
                                        }
                                        errors={errors}
                                        softLimit={surchargeDefaults?.surcharge_signature_debit_per_transaction?.soft_limit}
                                    />
                                    {errors?.surcharge_signature_debit_per_transaction &&
                                        Array.isArray(
                                            errors.surcharge_signature_debit_per_transaction,
                                        ) &&
                                        errors.surcharge_signature_debit_per_transaction.map(
                                            (error) => (
                                                <span className="error-message">
                                                    {error}
                                                </span>
                                            ),
                                        )}
                                </div>
                            </Stack>
                        </div>
                    )}

                    {form_data.pricing_model === "tiered" && (
                        <>
                            <div className="equipment-box mt-2r">
                                <p className="box-label">Qualified</p>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    className="container-col-2 mb-1r mt-2r"
                                >
                                    <div className="w-100">
                                        <PercentageInput
                                            label="Debit Rate"
                                            placeholder={surchargeDefaults.surcharge_qual_debit_rate?.default}
                                            name={"surcharge_qual_debit_rate"}
                                            type="number"
                                            onChange={handleOnChange}
                                            value={
                                                form_data.surcharge_qual_debit_rate
                                            }
                                            errors={errors}
                                            softLimit={surchargeDefaults?.surcharge_qual_debit_rate?.soft_limit}
                                        />
                                        {errors?.surcharge_qual_debit_rate &&
                                            Array.isArray(
                                                errors.surcharge_qual_debit_rate,
                                            ) &&
                                            errors.surcharge_qual_debit_rate.map(
                                                (error) => (
                                                    <span className="error-message">
                                                        {error}
                                                    </span>
                                                ),
                                            )}
                                    </div>
                                    <div className="w-100">
                                        <CurrencyInput
                                            label="Debit Txn Fee"
                                            placeholder={surchargeDefaults.surcharge_qual_debit_per_transaction?.default}
                                            name={
                                                "surcharge_qual_debit_per_transaction"
                                            }
                                            type="number"
                                            onChange={handleOnChange}
                                            value={
                                                form_data.surcharge_qual_debit_per_transaction
                                            }
                                            errors={errors}
                                            softLimit={surchargeDefaults?.surcharge_qual_debit_per_transaction?.soft_limit}
                                        />
                                        {errors?.surcharge_qual_debit_per_transaction &&
                                            Array.isArray(
                                                errors.surcharge_qual_debit_per_transaction,
                                            ) &&
                                            errors.surcharge_qual_debit_per_transaction.map(
                                                (error) => (
                                                    <span className="error-message">
                                                        {error}
                                                    </span>
                                                ),
                                            )}
                                    </div>
                                </Stack>
                            </div>
                            <div className="equipment-box mt-2r">
                                <p className="box-label">Mid-Qualified</p>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    className="container-col-2 mb-1r mt-2r"
                                >
                                    <div className="w-100">
                                        <PercentageInput
                                            label="Debit Rate"
                                            placeholder={surchargeDefaults.surcharge_mid_qual_debit_rate?.default}
                                            name={
                                                "surcharge_mid_qual_debit_rate"
                                            }
                                            type="number"
                                            onChange={handleOnChange}
                                            value={
                                                form_data.surcharge_mid_qual_debit_rate
                                            }
                                            errors={errors}
                                            softLimit={surchargeDefaults?.surcharge_mid_qual_debit_rate?.soft_limit}
                                        />
                                        {errors?.surcharge_mid_qual_debit_rate &&
                                            Array.isArray(
                                                errors.surcharge_mid_qual_debit_rate,
                                            ) &&
                                            errors.surcharge_mid_qual_debit_rate.map(
                                                (error) => (
                                                    <span className="error-message">
                                                        {error}
                                                    </span>
                                                ),
                                            )}
                                    </div>
                                    <div className="w-100">
                                        <CurrencyInput
                                            label="Debit Txn Fee"
                                            placeholder={surchargeDefaults.surcharge_mid_qual_debit_per_transaction?.default}
                                            name={
                                                "surcharge_mid_qual_debit_per_transaction"
                                            }
                                            type="number"
                                            onChange={handleOnChange}
                                            value={
                                                form_data.surcharge_mid_qual_debit_per_transaction
                                            }
                                            errors={errors}
                                            softLimit={surchargeDefaults?.surcharge_mid_qual_debit_per_transaction?.soft_limit}
                                        />
                                        {errors?.surcharge_mid_qual_debit_per_transaction &&
                                            Array.isArray(
                                                errors.surcharge_mid_qual_debit_per_transaction,
                                            ) &&
                                            errors.surcharge_mid_qual_debit_per_transaction.map(
                                                (error) => (
                                                    <span className="error-message">
                                                        {error}
                                                    </span>
                                                ),
                                            )}
                                    </div>
                                </Stack>
                            </div>
                            <div className="equipment-box mt-2r">
                                <p className="box-label">Non-Qualified</p>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    className="container-col-2 mb-1r mt-2r"
                                >
                                    <div className="w-100">
                                        <PercentageInput
                                            label="Debit Rate"
                                            placeholder={surchargeDefaults.surcharge_non_qual_debit_rate?.default}
                                            name={
                                                "surcharge_non_qual_debit_rate"
                                            }
                                            type="number"
                                            onChange={handleOnChange}
                                            value={
                                                form_data.surcharge_non_qual_debit_rate
                                            }
                                            errors={errors}
                                            softLimit={surchargeDefaults?.surcharge_non_qual_debit_rate?.soft_limit}
                                        />
                                        {errors?.surcharge_non_qual_debit_rate &&
                                            Array.isArray(
                                                errors.surcharge_non_qual_debit_rate,
                                            ) &&
                                            errors.surcharge_non_qual_debit_rate.map(
                                                (error) => (
                                                    <span className="error-message">
                                                        {error}
                                                    </span>
                                                ),
                                            )}
                                    </div>
                                    <div className="w-100">
                                        <CurrencyInput
                                            label="Debit Txn Fee"
                                            placeholder={surchargeDefaults.surcharge_non_qual_debit_per_transaction?.default}
                                            name={
                                                "surcharge_non_qual_debit_per_transaction"
                                            }
                                            type="number"
                                            onChange={handleOnChange}
                                            value={
                                                form_data.surcharge_non_qual_debit_per_transaction
                                            }
                                            errors={errors}
                                            softLimit={surchargeDefaults?.surcharge_non_qual_debit_per_transaction?.soft_limit}
                                        />
                                        {errors?.surcharge_non_qual_debit_per_transaction &&
                                            Array.isArray(
                                                errors.surcharge_non_qual_debit_per_transaction,
                                            ) &&
                                            errors.surcharge_non_qual_debit_per_transaction.map(
                                                (error) => (
                                                    <span className="error-message">
                                                        {error}
                                                    </span>
                                                ),
                                            )}
                                    </div>
                                </Stack>
                            </div>
                        </>
                    )}
                </>
            )}

            <div className="equipment-box mt-2r">
                <p className="box-label">PIN-based Debit</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-60">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) => {
                                setFormDataByKeyValue("surcharge_pin_debit_network_fee", value);
                                if (value === "no") {
                                    setFormDataByKeyValue("surcharge_pin_debit_rate", 0);
                                    setFormDataByKeyValue("surcharge_pin_debit_per_transaction", 0);
                                }
                                if (value === "yes") {
                                    setFormDataByKeyValue("surcharge_pin_debit_rate", surchargeDefaults.surcharge_pin_debit_rate?.default ?? 0);
                                    setFormDataByKeyValue("surcharge_pin_debit_per_transaction", surchargeDefaults.surcharge_pin_debit_per_transaction?.default ?? 0);
                                }
                            }}
                            value={form_data.surcharge_pin_debit_network_fee}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="surcharge_pin_debit_network_fee"
                            label="Network Fee"
                        />
                        {errors?.surcharge_pin_debit_network_fee &&
                            Array.isArray(
                                errors.surcharge_pin_debit_network_fee,
                            ) &&
                            errors.surcharge_pin_debit_network_fee.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                    <div className="w-100">
                        <PercentageInput
                            label="Debit Rate"
                            type="number"
                            placeholder={surchargeDefaults.surcharge_pin_debit_rate?.default}
                            name={"surcharge_pin_debit_rate"}
                            onChange={handleOnChange}
                            value={form_data.surcharge_pin_debit_rate}
                            errors={errors}
                            softLimit={surchargeDefaults?.surcharge_pin_debit_rate?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.surcharge_pin_debit_network_fee}
                        />
                        {errors?.surcharge_pin_debit_rate &&
                            Array.isArray(errors.surcharge_pin_debit_rate) &&
                            errors.surcharge_pin_debit_rate.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    <div className="w-100">
                        <CurrencyInput
                            label="Per Txn"
                            type="number"
                            placeholder={surchargeDefaults.surcharge_pin_debit_per_transaction?.default}
                            name={"surcharge_pin_debit_per_transaction"}
                            onChange={handleOnChange}
                            value={
                                form_data.surcharge_pin_debit_per_transaction
                            }
                            errors={errors}
                            softLimit={surchargeDefaults?.surcharge_pin_debit_per_transaction?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.surcharge_pin_debit_network_fee}
                        />
                        {errors?.surcharge_pin_debit_per_transaction &&
                            Array.isArray(
                                errors.surcharge_pin_debit_per_transaction,
                            ) &&
                            errors.surcharge_pin_debit_per_transaction.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                </Stack>
            </div>

            <div className="equipment-box mt-2r">
                <p className="box-label">ACH</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-100 d-flex">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("add_ach", value)
                            }
                            value={form_data.add_ach}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="add_ach"
                            label="Add ACH"
                        />
                    </div>
                </Stack>
            </div>

            {form_data.add_ach === "yes" && (
                <ACHPricing
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                    isAddOn={true}
                />
            )}

            <div className="equipment-box mt-2r ebt-transaction">
                <p className="box-label">EBT Transactions</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="ebt-switch">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("ebt", value)
                            }
                            value={form_data.ebt}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="ebt"
                            label="EBT"
                        />
                        {errors?.ebt &&
                            Array.isArray(errors.ebt) &&
                            errors.ebt.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    {form_data.ebt == "yes" && (
                        <div className="d-flex w-100">
                            <div className="floating-form w-100">
                                <Form.Group size="lg" controlId="fnf_number">
                                    <Form.Control
                                        placeholder="FNS Number"
                                        type="text"
                                        name="fnf_number"
                                        value={form_data.fnf_number}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                handleOnChange(e);
                                            }
                                        }}
                                        className="input-control"
                                    />
                                    <Form.Label className="floating-label">
                                        FNF Number
                                    </Form.Label>
                                </Form.Group>
                                {errors?.fnf_number &&
                                    Array.isArray(errors.fnf_number) &&
                                    errors.fnf_number.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="floating-form w-100">
                                <CurrencyInput
                                    label="EBT Transactions"
                                    endAdornment="/txn"
                                    placeholder={
                                        EMS_OTHER_DEFAULT_FEE.ebt_transactions
                                    }
                                    value={form_data.ebt_transactions}
                                    name={"ebt_transactions"}
                                    type="number"
                                    onChange={handleOnChange}
                                    errors={errors}
                                />
                                {errors?.ebt_transactions &&
                                    Array.isArray(errors.ebt_transactions) &&
                                    errors.ebt_transactions.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                </Stack>
            </div>

            <DefaultFee
                form_data={form_data}
                setFormDataByKeyValue={setFormDataByKeyValue}
                errors={errors}
            />

            <Equipments
                selectedEquipments={selectedEquipments}
                emsEquipments={emsEquipments}
                setFormDataByKeyValue={setFormDataByKeyValue}
                form_data={form_data}
                errors={errors}
                allEquipments={allEquipments}
                removeError={removeError}
            />
        </div>
    );
};

export default Surcharge;
